import { BehaviorSubject, Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { Injectable } from '@angular/core'

import { ModuleType } from './authentication.service'
import { EndpointOptions, RequestService } from './request.service'

export type SeatClass = 'admin' | 'read_only'

export interface KartoriumRole {
    id: string
    organization_id: string
    name: string
    description: string | null
    seat_class: SeatClass | null
    visible_modules: string[] | null
    is_default: boolean
}

export interface MyRoles {
    roles: KartoriumRole[]
    /** Union of visible_modules across the user's roles, or null if no role has an override. */
    visible_modules: string[] | null
    seat_class: SeatClass | null
}

const EMPTY: MyRoles = { roles: [], visible_modules: null, seat_class: null }

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private _roles = new BehaviorSubject<MyRoles | null>(null)
    public roles$: Observable<MyRoles | null> = this._roles.asObservable()

    constructor(private _requestService: RequestService) {}

    get currentRoles(): MyRoles | null {
        return this._roles.getValue()
    }

    /** Fetch the current user's Kartorium roles + effective settings. */
    refresh(): Observable<MyRoles> {
        const url = `${environment.api}/me/roles`
        const options = { error: { operation: 'Get My Roles' } } as EndpointOptions
        return this._requestService.get<MyRoles>(url, options).pipe(
            tap(res => this._roles.next(res ?? EMPTY)),
            catchError(() => {
                this._roles.next(EMPTY)
                return of(EMPTY)
            }),
            map(res => res ?? EMPTY),
        )
    }

    clear(): void {
        this._roles.next(null)
    }

    /**
     * Returns true if the user can access the module per the union of their roles'
     * visible_modules. Null override means "defer to other guards" -> returns true.
     */
    canAccess(module: ModuleType): boolean {
        const r = this._roles.getValue()
        if (r == null) return true
        if (r.visible_modules == null) return true
        return r.visible_modules.includes(module)
    }
}
